import { Fragment } from "react";
import { Inbox, PartyPopper, CheckCheck, SearchX } from "lucide-react";
import { catOf, sourceOf, dayBucket, fmtFull, relTime, senderName } from "./utils";

function Row({ g, active, onClick, isPlatformView }) {
  const f = g.first;
  const cat = catOf(f);
  const src = sourceOf(f);
  const unread = f.status === "new";
  return (
    <button data-testid={`feedback-row-${f.id}`} onClick={onClick} aria-selected={active}
      className={`w-full flex items-start gap-3 px-4 py-3.5 text-left border-l-[3px] border-b border-b-sand-100 transition-colors ${
        active ? "bg-redline-50 border-l-redline-500" : "border-l-transparent hover:bg-sand-50"}`}>
      <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${cat.cls}`} title={cat.label}>
        <cat.icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {unread && <span data-testid={`feedback-unread-${f.id}`} className="h-2 w-2 rounded-full bg-redline-500 shrink-0" />}
          <span className={`truncate text-sm ${unread ? "font-bold text-ink" : "font-semibold text-ink/75"}`}>{senderName(f)}</span>
          {g.items.length > 1 && (
            <span data-testid={`feedback-dupe-${f.id}`} title={`Said ${g.items.length} times`}
              className="text-[10px] font-bold rounded-full bg-ink text-white px-1.5 py-0.5 shrink-0 leading-none">×{g.items.length}</span>
          )}
          <span className="ml-auto text-[11px] text-ink/45 shrink-0 tabular-nums" title={fmtFull(f.created_at)}>{relTime(f.created_at)}</span>
        </div>
        <div className={`text-sm truncate mt-0.5 ${unread ? "text-ink/80" : "text-ink/55"}`}>{f.message}</div>
        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-ink/45 min-w-0">
          <src.icon className="h-3 w-3 shrink-0" /> <span className="shrink-0">{src.label}</span>
          {isPlatformView && f.community && <><span className="text-sand-300">·</span><span className="truncate">{f.community}</span></>}
        </div>
      </div>
    </button>
  );
}

function Empty({ box, query, doneCount, onShowDone, onShowInbox }) {
  const Icon = query ? SearchX : box === "new" ? PartyPopper : CheckCheck;
  return (
    <div data-testid="feedback-empty" className="p-12 text-center text-muted-foreground flex flex-col items-center gap-2">
      <Icon className={`h-8 w-8 ${box === "new" && !query ? "text-redline-400" : "text-sand-300"}`} />
      {query ? (
        <span>Nothing matches &ldquo;{query}&rdquo;.</span>
      ) : box === "new" ? (
        <>
          <span className="font-heading font-bold text-ink text-base">Inbox zero</span>
          <span className="text-sm">Every piece of feedback has been read.</span>
          {doneCount > 0 && (
            <button data-testid="feedback-empty-show-done" onClick={onShowDone} className="mt-1 text-sm underline underline-offset-2 text-ink hover:text-redline-600">
              See the {doneCount} you&rsquo;ve handled
            </button>
          )}
        </>
      ) : (
        <>
          <span>Nothing marked done yet.</span>
          <button data-testid="feedback-empty-show-inbox" onClick={onShowInbox} className="text-sm underline underline-offset-2 text-ink hover:text-redline-600">Back to the inbox</button>
        </>
      )}
    </div>
  );
}

export function FeedbackList({ groups, selectedId, onSelect, isPlatformView, box, query, counts, onBox, total, loaded, onLoadMore, loadingMore, loading }) {
  let lastBucket = null;
  return (
    <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
      <div className="max-h-[72vh] overflow-y-auto chat-scroll" data-testid="feedback-list">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
        ) : groups.length === 0 ? (
          <Empty box={box} query={query} doneCount={counts.done || 0} onShowDone={() => onBox("done")} onShowInbox={() => onBox("new")} />
        ) : groups.map((g) => {
          const b = dayBucket(g.first.created_at);
          const header = b !== lastBucket ? b : null;
          lastBucket = b;
          return (
            <Fragment key={g.id}>
              {header && (
                <div className="sticky top-0 z-10 bg-sand-50/95 backdrop-blur px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-ink/45 border-b border-sand-100">{header}</div>
              )}
              <Row g={g} active={g.id === selectedId} onClick={() => onSelect(g)} isPlatformView={isPlatformView} />
            </Fragment>
          );
        })}
        {!loading && total != null && loaded < total && (
          <div className="p-3 text-center">
            <button data-testid="feedback-load-more" onClick={onLoadMore} disabled={loadingMore}
              className="rounded-full border border-sand-300 bg-white px-4 py-2 text-xs font-bold text-ink/70 transition-colors hover:border-ink/30 disabled:opacity-50 inline-flex items-center gap-1.5">
              <Inbox className="h-3.5 w-3.5" /> {loadingMore ? "Loading…" : `Load ${Math.min(100, total - loaded)} older`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
